import React, { useState } from 'react';
import { Building, Key, ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { joinEnterprise } from '../services/enterprise';

const EnterpriseJoin = ({ onSuccess, onCancel }) => {
  const [enterpriseKey, setEnterpriseKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const formatKey = (key) => {
    // Remove existing hyphens and non-alphanumeric characters
    const cleaned = key.replace(/[^a-zA-Z0-9]/g, '');
    
    // Add hyphens every 4 characters if not already present
    if (cleaned.length <= 4) return cleaned;
    
    let formatted = '';
    for (let i = 0; i < cleaned.length; i += 4) {
      const chunk = cleaned.slice(i, i + 4);
      formatted += chunk;
      if (i + 4 < cleaned.length) formatted += '-';
    }
    
    return formatted;
  };
  
  const handleKeyChange = (e) => {
    const formattedKey = formatKey(e.target.value);
    setEnterpriseKey(formattedKey);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      // Remove any spaces or formatting
      const cleanKey = enterpriseKey.replace(/\s+/g, '');
      
      const result = await joinEnterprise(cleanKey);
      
      if (result.status === 'success') {
        setSuccess({
          enterpriseName: result.enterpriseName,
          enterpriseId: result.enterpriseId
        });
        
        // Call the onSuccess callback after 2 seconds
        setTimeout(() => {
          if (onSuccess) onSuccess(result);
        }, 2000);
      } else {
        setError(result.message || 'Failed to join enterprise.');
      }
    } catch (err) {
      console.error('Error joining enterprise:', err);
      setError(err.response?.data?.message || err.message || 'An error occurred while joining the enterprise.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6 max-w-md mx-auto">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 text-blue-600 mb-4">
          <Building size={24} />
        </div>
        <h2 className="text-2xl font-bold">Join Enterprise</h2>
        <p className="text-gray-600 mt-1">
          Enter your enterprise key to join your organization
        </p>
      </div>
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}
      
      {success ? (
        <div className="text-center">
          <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-green-700">Successfully joined enterprise!</p>
                <p className="text-gray-600 text-sm mt-1">
                  You are now a member of {success.enterpriseName}
                </p>
              </div>
            </div>
          </div>
          <p className="text-gray-600 text-sm">Redirecting to your account...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label htmlFor="enterpriseKey" className="block text-sm font-medium text-gray-700 mb-1">
              Enterprise Key
            </label>
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Key className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="enterpriseKey"
                name="enterpriseKey"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                value={enterpriseKey}
                onChange={handleKeyChange}
                className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md"
                required
              />
            </div>
            <p className="mt-2 text-sm text-gray-500">
              Enter the enterprise key provided by your administrator
            </p>
          </div>
          
          <div className="flex justify-between space-x-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            
            <button
              type="submit"
              disabled={isLoading || enterpriseKey.length < 10}
              className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : (
                <>
                  Join
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default EnterpriseJoin;